/**
 * ProjectCard
 * -----------
 * Reusable card component for displaying a research project.
 * Shows category tag, status badge, title, description, author avatar,
 * recent update snippet, and engagement stats (followers, updates).
 *
 * Props: { project } — a project object from data/projects.js
 */
import { Link } from 'react-router-dom';
import styles from './ProjectCard.module.css';
import { getStatusColor } from '../../data/projects';

const ProjectCard = ({ project }) => {
  const statusColors = getStatusColor(project.status);

  return (
    <Link to={`/projects/${project.slug}`} className={styles.cardLink}>
    <article className={styles.card}>
      {/* ── Gradient header bar (category color) ── */}
      <div className={styles.cardHeader} style={{ background: project.gradient }}>
        <div className={styles.headerMeta}>
          {/* Category tag */}
          <span className={styles.categoryTag}>{project.category}</span>

          {/* Status badge */}
          <span
            className={styles.statusBadge}
            style={{ background: statusColors.bg, color: statusColors.text }}
          >
            <span
              className={`${styles.statusDot} ${project.status === 'Live' ? styles.statusDotPulse : ''}`}
              style={{ background: statusColors.dot }}
            />
            {project.status}
          </span>
        </div>
      </div>

      {/* ── Card body ── */}
      <div className={styles.cardBody}>
        <h3 className={styles.title}>{project.title}</h3>
        <p className={styles.description}>{project.description}</p>

        {/* ── Recent update snippet ── */}
        <div className={styles.updateBox}>
          <span className={styles.updateLabel}>Latest update</span>
          <p className={styles.updateText}>"{project.recentUpdate}"</p>
        </div>

        {/* ── Tags ── */}
        <div className={styles.tags}>
          {project.tags.map((tag) => (
            <span key={tag} className={styles.tag}>{tag}</span>
          ))}
        </div>
      </div>

      {/* ── Card footer: author + stats ── */}
      <div className={styles.cardFooter}>
        <div className={styles.author}>
          {/* Avatar with initials */}
          <div
            className={styles.avatar}
            style={{ background: `${project.author.color}22`, color: project.author.color }}
          >
            {project.author.initials}
          </div>
          <div className={styles.authorInfo}>
            <span className={styles.authorName}>{project.author.name}</span>
            <span className={styles.authorInstitution}>{project.author.university}</span>
          </div>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <svg viewBox="0 0 16 16" fill="none" className={styles.statIcon}>
              <path d="M8 2C4.5 2 1.5 4.5 1.5 8c0 1.5.5 2.9 1.4 4L2 14l2.1-.8C5.3 14 6.6 14.5 8 14.5c3.5 0 6.5-2.5 6.5-6S11.5 2 8 2z" stroke="currentColor" strokeWidth="1.3"/>
            </svg>
            <span>{project.followers.toLocaleString()}</span>
          </div>
          <div className={styles.stat}>
            <svg viewBox="0 0 16 16" fill="none" className={styles.statIcon}>
              <path d="M8 1.5v4M8 10.5V13M3 8H1M15 8h-2M4.5 4.5L3 3M13 13l-1.5-1.5M4.5 11.5L3 13M13 3l-1.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            <span>{project.updates} updates</span>
          </div>
        </div>
      </div>
    </article>
    </Link>
  );
};

export default ProjectCard;
